import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonSpinner } from '../ui/Loader';

interface SyriatelClaimDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onClaim: (transactionId: string) => Promise<void>;
  isLoading?: boolean;
  invoiceId: string;
}

export function SyriatelClaimDialog({
  isOpen,
  onClose,
  onClaim,
  isLoading = false,
  invoiceId,
}: SyriatelClaimDialogProps) {
  const { t } = useTranslation();
  const [transactionId, setTransactionId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmed = transactionId.trim();

    if (!trimmed) {
      setError(t('recharge.syriatelClaim.transactionIdRequired'));
      return;
    }

    if (trimmed.length < 12 || !/^\d+$/.test(trimmed)) {
      setError(t('recharge.syriatelClaim.transactionIdInvalid'));
      return;
    }

    try {
      await onClaim(trimmed);
      setTransactionId('');
      onClose();
    } catch (err) {
      // Error is handled by parent component
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setTransactionId('');
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div className="relative card-glass rounded-2xl shadow-2xl w-full max-w-md animate-scale-in">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-heading font-bold text-brand-accent">
              {t('recharge.syriatelClaim.title')}
            </h3>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="text-neutral-medium hover:text-neutral-light transition-colors disabled:opacity-50"
              aria-label={t('common.close')}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <p className="text-sm text-neutral-medium font-body mb-4">
                {t('recharge.syriatelClaim.description')}
              </p>
              
              <label className="block text-sm font-medium text-neutral-light mb-2 font-body">
                {t('recharge.syriatelClaim.invoiceId')}
              </label>
              <input
                type="text"
                value={invoiceId}
                disabled
                className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-medium font-mono text-sm font-body cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-light mb-2 font-body">
                {t('recharge.syriatelClaim.transactionId')} *
              </label>
              <input
                type="text"
                value={transactionId}
                onChange={(e) => {
                  setTransactionId(e.target.value);
                  setError(null);
                }}
                disabled={isLoading}
                placeholder={t('recharge.syriatelClaim.transactionIdPlaceholder')}
                className={`w-full px-4 py-2 bg-neutral-dark border rounded-lg text-neutral-light font-body focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-300 ${
                  error ? 'border-danger' : 'border-neutral-dark'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                autoFocus
              />
              {error && (
                <p className="mt-1 text-sm text-danger font-body">{error}</p>
              )}
              <p className="mt-1 text-xs text-neutral-medium font-body">
                {t('recharge.syriatelClaim.transactionIdHint')}
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isLoading || !transactionId.trim()}
                className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <ButtonSpinner size="xs" />
                    <span>{t('common.loading')}</span>
                  </>
                ) : (
                  t('recharge.syriatelClaim.verify')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
